import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  crmService,
  datetimeLocalToIso,
  isoToDatetimeLocalValue,
  Task,
  TenantUserOption,
} from '../services/crmService';
import { ListPageHeader } from '../components/crm/ListPageHeader';
import { CrmEmptyState } from '../components/crm/EmptyState';
import { DataListTable, Column } from '../components/crm/DataListTable';
import { RowActionsMenu } from '../components/crm/RowActionsMenu';
import { useAuth } from '../contexts/AuthContext';

type ScopeFilter = 'open' | 'completed' | 'all';
type DueFilter = '' | 'overdue' | 'today' | 'week';

const PRIORITY_LABELS: Record<Task['priority'], string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const STATUS_LABELS: Record<Task['status'], string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const PRIORITY_CLASSES: Record<Task['priority'], string> = {
  urgent: 'bg-danger/15 text-danger-fg',
  high: 'bg-warning/20 text-warning-fg',
  medium: 'bg-surface-muted text-fg-secondary',
  low: 'bg-surface-muted text-fg-muted',
};

function userLabel(u: TenantUserOption): string {
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
}

function formatDue(iso: string | null): string {
  if (!iso) return 'No due date';
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isOverdue(task: Task): boolean {
  if (!task.due_date || task.status === 'completed' || task.status === 'cancelled') return false;
  return new Date(task.due_date).getTime() < Date.now();
}

/**
 * Full-tier Tasks page. `/tasks` previously redirected non-Essentials tenants to
 * the dashboard even though the nav advertised the link and `/api/tasks/` existed.
 */
const Tasks: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<TenantUserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [scope, setScope] = useState<ScopeFilter>('open');
  const [due, setDue] = useState<DueFilter>('');
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('');
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState<Task | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await crmService.getTasks({
        open: scope === 'open' ? '1' : undefined,
        status: scope === 'completed' ? 'completed' : undefined,
        due: due || undefined,
        assigned_to: assignee || undefined,
        priority: priority || undefined,
        search: search.trim() || undefined,
      });
      setTasks(res.results ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [scope, due, assignee, priority, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    crmService
      .getTenantUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  const setStatus = async (task: Task, status: Task['status']) => {
    try {
      await crmService.updateTask(task.id, { status });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update task');
    }
  };

  const remove = async (task: Task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await crmService.deleteTask(task.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete task');
    }
  };

  const columns: Column<Task>[] = useMemo(
    () => [
      {
        id: 'title',
        header: 'Task',
        mobilePriority: 'primary',
        render: (task) => (
          <div className="min-w-0">
            <div
              className={`font-medium ${
                task.status === 'completed' ? 'text-fg-muted line-through' : 'text-fg'
              }`}
            >
              {task.title}
            </div>
            {task.description ? (
              <div className="truncate text-xs text-fg-muted">{task.description}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'contact',
        header: 'Contact',
        mobileLabel: 'Contact',
        render: (task) =>
          task.client ? (
            <Link to={`/leads?pipeline_contact=${task.client}`} className="text-primary hover:underline">
              {task.client_name || 'View contact'}
            </Link>
          ) : (
            <span className="text-fg-muted">—</span>
          ),
      },
      {
        id: 'due',
        header: 'Due',
        mobileLabel: 'Due',
        render: (task) => (
          <span
            className={`text-sm tabular-nums ${
              isOverdue(task) ? 'font-semibold text-danger-fg' : 'text-fg-secondary'
            }`}
          >
            {isOverdue(task) ? 'Overdue · ' : ''}
            {formatDue(task.due_date)}
          </span>
        ),
      },
      {
        id: 'priority',
        header: 'Priority',
        mobileLabel: 'Priority',
        render: (task) => (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_CLASSES[task.priority]}`}>
            {PRIORITY_LABELS[task.priority]}
          </span>
        ),
      },
      {
        id: 'assignee',
        header: 'Assignee',
        mobileLabel: 'Assignee',
        render: (task) => <span className="text-sm text-fg-secondary">{task.assigned_to_name || '—'}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (task) => <span className="text-sm text-fg-secondary">{STATUS_LABELS[task.status]}</span>,
      },
      {
        id: 'actions',
        header: '',
        className: 'w-24',
        cellClassName: 'text-right',
        render: (task) => (
          <RowActionsMenu
            items={[
              task.status === 'completed'
                ? { id: 'reopen', label: 'Reopen', onClick: () => setStatus(task, 'pending') }
                : { id: 'complete', label: 'Mark complete', onClick: () => setStatus(task, 'completed') },
              { id: 'edit', label: 'Edit', onClick: () => setEditing(task) },
              { id: 'delete', label: 'Delete', onClick: () => remove(task) },
            ]}
          />
        ),
      },
    ],
    // setStatus/remove are stable enough for this list; load() drives refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const filtersActive = Boolean(due || assignee || priority || search.trim()) || scope !== 'open';

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Tasks"
        subtitle="Everything you and your team owe a contact — due dates, priorities, and follow-through."
        actions={
          <button type="button" onClick={() => setCreating(true)} className="btn-primary rounded-xl px-4 py-2 text-sm">
            New task
          </button>
        }
      />

      {error ? <div className="alert-danger rounded-md px-4 py-3 text-sm">{error}</div> : null}

      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Scope"
          value={scope}
          onChange={(e) => setScope(e.target.value as ScopeFilter)}
          className="rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg"
        >
          <option value="open">Open</option>
          <option value="completed">Completed</option>
          <option value="all">All</option>
        </select>
        <select
          aria-label="Due"
          value={due}
          onChange={(e) => setDue(e.target.value as DueFilter)}
          className="rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg"
        >
          <option value="">Any due date</option>
          <option value="overdue">Overdue</option>
          <option value="today">Due today</option>
          <option value="week">Next 7 days</option>
        </select>
        <select
          aria-label="Assignee"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          className="rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg"
        >
          <option value="">Anyone</option>
          <option value="me">Assigned to me</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {userLabel(u)}
            </option>
          ))}
        </select>
        <select
          aria-label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg"
        >
          <option value="">Any priority</option>
          {(['urgent', 'high', 'medium', 'low'] as const).map((p) => (
            <option key={p} value={p}>
              {PRIORITY_LABELS[p]}
            </option>
          ))}
        </select>
        <input
          type="search"
          aria-label="Search tasks"
          placeholder="Search tasks"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-[12rem] flex-1 rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg"
        />
      </div>

      <DataListTable
        columns={columns}
        rows={tasks}
        getRowKey={(task) => task.id}
        loading={loading}
        emptyContent={
          filtersActive ? (
            <CrmEmptyState
              title="No tasks match these filters"
              description="Try widening the scope or clearing the search."
              actionLabel="Clear filters"
              onAction={() => {
                setScope('open');
                setDue('');
                setAssignee('');
                setPriority('');
                setSearch('');
              }}
            />
          ) : (
            <CrmEmptyState
              title="No open tasks"
              description="Create a task to track a call, quote, or follow-up you owe a contact."
              actionLabel="New task"
              onAction={() => setCreating(true)}
            />
          )
        }
      />

      {(creating || editing) && (
        <TaskFormModal
          task={editing}
          users={users}
          defaultAssignee={user?.id ? String(user.id) : ''}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
};

interface TaskFormModalProps {
  /** null when creating. */
  task: Task | null;
  users: TenantUserOption[];
  defaultAssignee: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const TaskFormModal: React.FC<TaskFormModalProps> = ({ task, users, defaultAssignee, onClose, onSaved }) => {
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [assignedTo, setAssignedTo] = useState(task?.assigned_to ?? defaultAssignee);
  const [priority, setPriority] = useState<Task['priority']>(task?.priority ?? 'medium');
  const [status, setStatus] = useState<Task['status']>(task?.status ?? 'pending');
  const [dueLocal, setDueLocal] = useState(isoToDatetimeLocalValue(task?.due_date ?? null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = Boolean(title.trim() && assignedTo);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        assigned_to: assignedTo,
        priority,
        status,
        due_date: datetimeLocalToIso(dueLocal),
      };
      if (task) {
        await crmService.updateTask(task.id, payload);
      } else {
        await crmService.createTask(payload);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="relative top-10 mx-auto w-full max-w-xl rounded-2xl border border-border bg-surface p-5 shadow-lg">
        <h3 className="mb-4 text-lg font-semibold text-fg">{task ? 'Edit task' : 'New task'}</h3>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-fg-secondary" htmlFor="task-title">
              Title *
            </label>
            <input
              id="task-title"
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-xl border border-border bg-input px-3 py-3 text-fg"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-fg-secondary" htmlFor="task-description">
              Description
            </label>
            <textarea
              id="task-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-xl border border-border bg-input px-3 py-3 text-fg"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-fg-secondary" htmlFor="task-assignee">
                Assignee *
              </label>
              <select
                id="task-assignee"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full rounded-xl border border-border bg-input px-3 py-3 text-fg"
              >
                <option value="">Select a teammate</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {userLabel(u)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-fg-secondary" htmlFor="task-due">
                Due
              </label>
              <input
                id="task-due"
                type="datetime-local"
                value={dueLocal}
                onChange={(e) => setDueLocal(e.target.value)}
                className="w-full rounded-xl border border-border bg-input px-3 py-3 text-fg"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-fg-secondary" htmlFor="task-priority">
                Priority
              </label>
              <select
                id="task-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as Task['priority'])}
                className="w-full rounded-xl border border-border bg-input px-3 py-3 text-fg"
              >
                {(['urgent', 'high', 'medium', 'low'] as const).map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-fg-secondary" htmlFor="task-status">
                Status
              </label>
              <select
                id="task-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as Task['status'])}
                className="w-full rounded-xl border border-border bg-input px-3 py-3 text-fg"
              >
                {(['pending', 'in_progress', 'completed', 'cancelled'] as const).map((sv) => (
                  <option key={sv} value={sv}>
                    {STATUS_LABELS[sv]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error ? (
            <p role="alert" className="rounded-xl border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger-fg">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-surface-muted px-4 py-2">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || saving}
              className="btn-primary rounded-xl px-4 py-2 text-sm disabled:opacity-50"
            >
              {saving ? 'Saving…' : task ? 'Save changes' : 'Create task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Tasks;
